import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Typography,
} from '@mui/material';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { WO2Checkbox, WO2Select, WO2ToggleSwitch } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { BenchmarkSelect } from 'src/features/securities/performance/components/benchmarkSelect';
import { SecurityBenchmark } from 'src/features/securities/performance/store/types';
import { DateRange } from 'src/store';
import {
  BaseReportComponentProps,
  HidingOptionPortfolioPerformance,
  HidingOptionsRequest,
  MdaReportTypeField,
  MdaReportTypes,
  PerformanceMethodology,
  PeriodBaseRequest,
} from '../../store/types';
import { MultiplePeriodSelector } from '../MultiplePeriodSelector';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface PorfolioPerformanceProps extends BaseReportComponentProps {
  setPortfolioPerformanceSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangePortfolioPerformanceParameter: ActionCreatorWithPayload<DateRange>;
  setPortfolioPerformanceHidingOption: ActionCreatorWithPayload<HidingOptionsRequest<HidingOptionPortfolioPerformance>, string>;
  setPortfolioPerformanceMethodology: ActionCreatorWithPayload<PerformanceMethodology, string>;
  expandPortfolioPerformance: ActionCreatorWithoutPayload<string>;
  setPortfolioPerformanceBenchmarkParameter: ActionCreatorWithPayload<number>;
  benchmarks: SecurityBenchmark[];
  setPortfolioPerformanceMultiplePeriods: ActionCreatorWithPayload<PeriodBaseRequest[], string>;
}

export const PorfolioPerformance = ({
  parameters,
  setPortfolioPerformanceSelectedParameter,
  setDateRangePortfolioPerformanceParameter,
  setPortfolioPerformanceHidingOption,
  setPortfolioPerformanceBenchmarkParameter,
  setPortfolioPerformanceMethodology,
  setPortfolioPerformanceMultiplePeriods,
  benchmarks,
  expandPortfolioPerformance,
  createReportSortRequest,
  setGroupConsolidatedOption,
  isDatesLessThanFullYear,
}: PorfolioPerformanceProps) => {
  const handleClickPortfolioPerformanceSelect = () => {
    setPortfolioPerformanceSelectedParameter(!parameters.selectedReports?.portfolioPerformance.selected);
    createReportSortRequest(
      !parameters.selectedReports?.portfolioPerformance.selected,
      MdaReportTypes.PortfolioPerformance.id,
      MdaReportTypes.PortfolioPerformance.displayName
    );
  };

  const handleClickDateForPortfolioPerformance = (dates: DateRange) => {
    setDateRangePortfolioPerformanceParameter(dates);
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(dates)) {
      setPortfolioPerformanceHidingOption({
        option: HidingOptionPortfolioPerformance.AnnualisedReturns,
        value: false,
      });
    }
  };

  const setBenchmark = (benchmark: SecurityBenchmark | null) => {
    setPortfolioPerformanceBenchmarkParameter(benchmark?.id ?? -1);
  };

  useEffect(() => {
    if (isDatesLessThanFullYear && isDatesLessThanFullYear(parameters.dates)) {
      setPortfolioPerformanceHidingOption({
        option: HidingOptionPortfolioPerformance.AnnualisedReturns,
        value: false,
      });
    }
  }, [parameters.dates]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioPerformance.benchmarkId !== -1) {
      setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.PrimaryBenchmark, value: false });
      setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.SecondaryBenchmark, value: false });
    }
  }, [parameters.selectedReports.portfolioPerformance.benchmarkId]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioPerformance.primaryBenchmark) {
      setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.SecondaryBenchmark, value: false });
      setBenchmark(null);
    }
  }, [parameters.selectedReports.portfolioPerformance.primaryBenchmark]);

  useEffect(() => {
    if (parameters.selectedReports.portfolioPerformance.secondaryBenchmark) {
      setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.PrimaryBenchmark, value: false });
      setBenchmark(null);
    }
  }, [parameters.selectedReports.portfolioPerformance.secondaryBenchmark]);

  return (
    <Accordion onChange={expandPortfolioPerformance} expanded={parameters.selectedReports?.portfolioPerformance.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          aria-label="Acknowledge"
          sx={{ flex: 1 }}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox
              color="primary"
              checked={parameters.selectedReports?.portfolioPerformance.selected}
              onChangeHandler={handleClickPortfolioPerformanceSelect}
            />
          }
          label="Portfolio Performance"
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: parameters?.selectedReports.portfolioPerformance?.fromDate,
              dateTo: parameters?.selectedReports.portfolioPerformance?.toDate,
              dateRangeType: parameters?.selectedReports.portfolioPerformance?.dateRangeType,
            }}
            setDateRange={handleClickDateForPortfolioPerformance}
            inceptionDate={parameters.inceptionDate}
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <MultiplePeriodSelector
              maxPeriods={3}
              disableInceptionRemoval={false}
              onChange={(periods: PeriodBaseRequest[]) => {
                setPortfolioPerformanceMultiplePeriods(periods);
              }}
              values={parameters.selectedReports.portfolioPerformance.multiPeriods || []}
            />
          </Grid>
          <Grid item container spacing={1} alignItems="center">
            <Grid item>
              <FormControl fullWidth>
                <BenchmarkSelect
                  setBenchmark={setBenchmark}
                  selectedItem={benchmarks.find((benchmark) => benchmark.id === parameters.selectedReports.portfolioPerformance.benchmarkId) ?? null}
                  disabled={
                    parameters.selectedReports.portfolioPerformance.primaryBenchmark ||
                    parameters.selectedReports.portfolioPerformance.secondaryBenchmark
                  }
                  items={benchmarks}
                />
              </FormControl>
            </Grid>

            <Grid item>
              <FormControl fullWidth>
                <InputLabel id="performance-methodology">Performance Methodology</InputLabel>
                <WO2Select
                  labelId="performance-methodology"
                  label="performance methodology"
                  onChange={(e) => setPortfolioPerformanceMethodology(e.target.value as PerformanceMethodology)}
                  value={parameters.selectedReports.portfolioPerformance.performanceMethodology}
                >
                  <MenuItem value={PerformanceMethodology.TimeWeighted}>Time Weighted Return</MenuItem>
                  <MenuItem value={PerformanceMethodology.ReturnOnAverage}>Return on Average Value</MenuItem>
                </WO2Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid item container direction="row" alignItems="center" spacing={1} mt={1}>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.excludeFrankingCredits}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({
                    option: HidingOptionPortfolioPerformance.FrankingCredits,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Exclude Franking Credits</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.showAnnualisedReturns}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({
                    option: HidingOptionPortfolioPerformance.AnnualisedReturns,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  isDatesLessThanFullYear &&
                  isDatesLessThanFullYear({
                    dateFrom: parameters?.selectedReports.portfolioPerformance?.fromDate,
                    dateTo: parameters?.selectedReports.portfolioPerformance?.toDate,
                    dateRangeType: parameters?.selectedReports.portfolioPerformance?.dateRangeType,
                  })
                }
              />
              <Typography variant="h4">Show Annualised Returns</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.applyExclusionList}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({
                    option: HidingOptionPortfolioPerformance.ExclusionList,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Apply Exclusion List</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.includeAfterFeesReturn}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({
                    option: HidingOptionPortfolioPerformance.AfterFeesReturn,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Include After Fees Return</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3} flexWrap="nowrap">
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.includeNetContributions}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({
                    option: HidingOptionPortfolioPerformance.NetContributions,
                    value: e.target.checked,
                  })
                }
                color="primary"
                name="hidingOptions"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
              <Typography variant="h4">Include Net Contributions</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.primaryBenchmark}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.PrimaryBenchmark, value: e.target.checked })
                }
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  parameters.selectedReports.portfolioPerformance.secondaryBenchmark ||
                  parameters.selectedReports.portfolioPerformance.benchmarkId !== -1
                }
              />
              <Typography variant="h4">Use Primary Benchmark</Typography>
            </Grid>
            <Grid item container alignItems="center" sm={6} md={3}>
              <WO2ToggleSwitch
                checked={parameters.selectedReports.portfolioPerformance.secondaryBenchmark}
                onChange={(e) =>
                  setPortfolioPerformanceHidingOption({ option: HidingOptionPortfolioPerformance.SecondaryBenchmark, value: e.target.checked })
                }
                color="primary"
                inputProps={{ 'aria-label': 'primary checkbox' }}
                disabled={
                  parameters.selectedReports.portfolioPerformance.primaryBenchmark ||
                  parameters.selectedReports.portfolioPerformance.benchmarkId !== -1
                }
              />
              <Typography variant="h4">Use Secondary Benchmark</Typography>
            </Grid>
          </Grid>
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column" mt={1}>
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={parameters.selectedReports.portfolioPerformance.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.PortfolioPerformance}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
